import { queryOptions } from "@tanstack/react-query";
import { axiosInstance } from "./axios";
import { z } from "zod";

const timeOnIceSchema = z.object({
    offensive: z.number(),
    defensive: z.number(),
    neutral: z.number(),
    total: z.number(),
});

const timeOnIcePercentageSchema = z.object({
    offensive: z.number(),
    defensive: z.number(),
    neutral: z.number(),
    total: z.number().nullable(),
});

const zoneDataSchema = z.object({
    offensive: z.number(),
    defensive: z.number(),
    neutral: z.number(),
    total: z.number().nullable(),
});

const timeDataSchema = z.object({
    timeOnIce: timeOnIceSchema,
    timeOnIcePercentage: timeOnIcePercentageSchema.nullable(),
    averageShiftLength: z.number(),
});

const defensiveDataSchema = z.object({
    points: z.number(),
    highRiskShotsAgainst: z.number(),
    pressureGiven: z.number(),
    xgAgainst: z.number(),
    blockedShots: z.number(),
    puckClearances: z.number(),
    contests: zoneDataSchema,
    contestsWon: zoneDataSchema,
    puckContestsWinRate: z.number(),
});

const shotsSchema = z.object({
    accurateShots: z.number(),
    missedShots: z.number(),
    totalShots: z.number(),
});

const successfulPassesSchema = z.object({
    successfulPassesData: z.number(),
    successfulPassesUnderPressure: z.number(),
    successfulPassesUnderHighPressure: z.number(),
});

const failedPassesSchema = z.object({
    failedPassesData: z.number(),
    failedPassesUnderPressure: z.number(),
    failedPassesUnderHighPressure: z.number(),
});

const offensiveDataSchema = z.object({
    points: z.number(),
    shots: shotsSchema,
    successfulPasses: successfulPassesSchema,
    failedPasses: failedPassesSchema,
    totalXg: z.number(),
    xgFor: z.number(),
    pressureReceived: z.number(),
    puckProtectionPPM: zoneDataSchema,
    puckProtectionEvents: zoneDataSchema,
});

const teamPlayDataSchema = z.object({
    points: z.number(),
    plusMinusTotal: z.number(),
    corsiForPercentage: z.number(),
    relativeCorsiForPercentage: z.number(),
    fenwickForPercentage: z.number(),
    relativeFenwickForPercentage: z.number(),
    penaltyMinutes: z.number(),
    takeaways: zoneDataSchema,
    turnovers: zoneDataSchema,
});

const puckCarryingSchema = z.object({
    puckControlForwardDistance: z.number(),
    puckControlForwardDistancePercentage: z.number(),
});

const effortDataSchema = z.object({
    points: z.number(),
    topSpeed: z.number(),
    distanceTravelled: z.number(),
    averageSpeedWithPuck: z.number(),
    accelerationsPer20: z.number(),
    decelerationsPer20: z.number(),
    puckCarrying: puckCarryingSchema,
});

const bonusDataSchema = z.object({
    points: z.number(),
    goals: z.number(),
    assists: z.number(),
});

const playerKpiSchema = z.object({
    matchDate: z.string(),
    awayTeam: z.string(),
    awayGoals: z.number(),
    homeTeam: z.string(),
    homeGoals: z.number(),
    homeTeamId: z.number().optional(),
    awayTeamId: z.number().optional(),
    role: z.string(),
    shotsAgainstHome: z.number(),
    shotsAgainstAway: z.number(),
    defensiveData: defensiveDataSchema,
    offensiveData: offensiveDataSchema,
    teamPlayData: teamPlayDataSchema,
    effortData: effortDataSchema,
    bonusData: bonusDataSchema,
    timeData: timeDataSchema,
    totalPoints: z.number(),
});

const pointsGSAxSchema = z.object({
    pointsGSAx: z.number().nullable(),
    gsAx: z.number().nullable(),
    ga: z.number().nullable(),
    xg_GA: z.number().nullable(),
    savePercentage: z.number().nullable(),
});

const zone1Schema = z.object({
    pointsHighDangerShots: z.number().nullable(),
    gaZ1: z.number().nullable(),
    xg_GAZ1: z.number().nullable(),
    savePercentageZ1: z.number().nullable(),
});

const zone4Schema = z.object({
    pointsLowDangerShots: z.number().nullable(),
    gaZ4: z.number().nullable(),
    xg_GAZ4: z.number().nullable(),
    savePercentageZ4: z.number().nullable(),
});

const screenedSchema = z.object({
    pointsScreenedShots: z.number().nullable(),
    gaScreened: z.number().nullable(),
    xg_GAScreened: z.number().nullable(),
    savePercentageScreened: z.number().nullable(),
});

const lateralPassSchema = z.object({
    pointsLateralPassShots: z.number().nullable(),
    gaLateralPass: z.number().nullable(),
    xg_GALateralPass: z.number().nullable(),
    savePercentageLateralPass: z.number().nullable(),
});

const goalkeeperKpiSchema = z.object({
    matchDate: z.string(),
    awayTeam: z.string(),
    awayGoals: z.number(),
    homeTeam: z.string(),
    homeGoals: z.number(),
    homeTeamId: z.number().optional(),
    awayTeamId: z.number().optional(),
    role: z.string(),
    pointsGSAx: pointsGSAxSchema,
    zone1: zone1Schema,
    zone4: zone4Schema,
    screened: screenedSchema,
    lateralPass: lateralPassSchema,
    totalPoints: z.number(),
});

const kpiLeagueAvgsGoalkeeperSchema = z.object({
    pointsGSAxAvg: z.number().nullable(),
    pointsHighDangerShotsAvg: z.number().nullable(),
    pointsLowDangerShotsAvg: z.number().nullable(),
    pointsScreenedShotsAvg: z.number().nullable(),
    pointsLateralPassShotsAvg: z.number().nullable(),
    totalPointsAvg: z.number().nullable(),
});

const kpiLeagueAvgsSchema = z.object({
    defensivePointsAvg: z.number().nullable(),
    offensivePointsAvg: z.number().nullable(),
    teamPlayPointsAvg: z.number().nullable(),
    effortPointsAvg: z.number().nullable(),
    bonusPointsAvg: z.number().nullable(),
    totalPointsAvg: z.number().nullable(),
});

const kpiPercentileRanksSchema = z.object({
    defensive: z.number().nullable(),
    offensive: z.number().nullable(),
    teamPlay: z.number().nullable(),
    effort: z.number().nullable(),
    bonus: z.number().nullable(),
    total: z.number().nullable(),
});

const kpiPercentileRanksGoalkeeperSchema = z.object({
    gsAx: z.number().nullable(),
    highDanger: z.number().nullable(),
    lowDanger: z.number().nullable(),
    lateralPass: z.number().nullable(),
    screened: z.number().nullable(),
    total: z.number().nullable(),
});

const kpiPerRoleSchema = z.object({
    role: z.string().nullable(),
    teamId: z.number().optional(),
    matchesPlayed: z.number().nullable(),
    timeOnIce: z.number().nullable(),
    avgKpi: z.number().nullable(),
    avgDefensivePoints: z.number().nullable().optional(),
    avgOffensivePoints: z.number().nullable().optional(),
    avgTeamPlayPoints: z.number().nullable().optional(),
    avgEffortPoints: z.number().nullable().optional(),
    avgBonusPoints: z.number().nullable().optional(),
    avgPointsGSAx: z.number().nullable().optional(),
    avgPointsHighDangerShots: z.number().nullable().optional(),
    avgPointsLowDangerShots: z.number().nullable().optional(),
    avgPointsLateralPassShots: z.number().nullable().optional(),
    avgPointsScreenedShots: z.number().nullable().optional(),
    goals: z.number().nullable().optional(),
    shots: z.number().nullable().optional(),
    plusMinusTotal: z.number().nullable().optional(),
    blockedShots: z.number().nullable().optional(),
    corsiForPercentage: z.number().nullable().optional(),
    fenwickForPercentage: z.number().nullable().optional(),
    topSpeed: z.number().nullable().optional(),
    distanceTravelled: z.number().nullable().optional(),
    averageSpeedWithPuck: z.number().nullable().optional(),
    puckContestsWon: z.number().nullable().optional(),
    puckContestParticipations: z.number().nullable().optional(),
    faceoffWins: z.number().nullable().optional(),
    faceoffCount: z.number().nullable().optional(),
    averagePressureGiven: z.number().nullable().optional(),
    averagePressureReceived: z.number().nullable().optional(),
    savePercentage: z.number().nullable().optional(),
    goalsAgainstAverage: z.number().nullable().optional(),
    saves: z.number().nullable().optional(),
    goalsAgainst: z.number().nullable().optional(),
    shotsAgainstWithScreen: z.number().nullable().optional(),
    goalsAgainstWithScreen: z.number().nullable().optional(),
});

const playerDataSchema = z.object({
    tournamentId: z.number().nullable(),
    teamId: z.number().optional(),
    firstName: z.string(),
    lastName: z.string(),
    role: z.array(z.string()),
    jerseyNumber: z.number().optional(),
    goalkeeperKpis: z.array(goalkeeperKpiSchema).optional(),
    kpiLeagueAvgsGoalkeeper: kpiLeagueAvgsGoalkeeperSchema.optional().nullable(),
    kpiPercentileRanksGoalkeeper: kpiPercentileRanksGoalkeeperSchema.optional().nullable(),
    kpis: z.array(playerKpiSchema).optional(),
    kpiLeagueAvgs: kpiLeagueAvgsSchema.optional().nullable(),
    kpiPercentileRanks: kpiPercentileRanksSchema.optional().nullable(),
    kpiPerRole: z.array(kpiPerRoleSchema).optional(),
});

export type TimeOnIce = z.infer<typeof timeOnIceSchema>;
export type TimeOnIcePercentage = z.infer<typeof timeOnIcePercentageSchema>;
export type ZoneData = z.infer<typeof zoneDataSchema>;
export type TimeData = z.infer<typeof timeDataSchema>;
export type Shots = z.infer<typeof shotsSchema>;
export type SuccessfulPasses = z.infer<typeof successfulPassesSchema>;
export type FailedPasses = z.infer<typeof failedPassesSchema>;
export type PuckCarrying = z.infer<typeof puckCarryingSchema>;
export type DefensiveData = z.infer<typeof defensiveDataSchema>;
export type OffensiveData = z.infer<typeof offensiveDataSchema>;
export type TeamPlayData = z.infer<typeof teamPlayDataSchema>;
export type EffortData = z.infer<typeof effortDataSchema>;
export type BonusData = z.infer<typeof bonusDataSchema>;
export type PlayerKpi = z.infer<typeof playerKpiSchema>;
export type PointsGSAx = z.infer<typeof pointsGSAxSchema>;
export type Zone1 = z.infer<typeof zone1Schema>;
export type Zone4 = z.infer<typeof zone4Schema>;
export type Screened = z.infer<typeof screenedSchema>;
export type LateralPass = z.infer<typeof lateralPassSchema>;
export type GoalkeeperKpi = z.infer<typeof goalkeeperKpiSchema>;
export type KpiLeagueAvgsGoalkeeper = z.infer<typeof kpiLeagueAvgsGoalkeeperSchema>;
export type KpiLeagueAvgs = z.infer<typeof kpiLeagueAvgsSchema>;
export type KpiAvgsGoalkeeper = KpiLeagueAvgsGoalkeeper;
export type KpiAvgs = KpiLeagueAvgs;
export type KpiPerRole = z.infer<typeof kpiPerRoleSchema>;
export type PlayerData = z.infer<typeof playerDataSchema>;

export interface PlayerMetrics {
    avgPointsPerGame: number;
    totalGames: number;
    totalPoints: number;
    avgOffensivePoints: number;
    avgDefensivePoints: number;
    avgTeamPlayPoints: number;
    avgEffortPoints: number;
    avgBonusPoints: number;
    bestMatchPoints: number;
    consistencyScore: number;
}

export interface GoalkeeperMetrics {
    avgPointsPerGame: number;
    totalGames: number;
    totalPoints: number;
    avgGSAxPoints: number;
    avgHighDangerPoints: number;
    avgLowDangerPoints: number;
    avgScreenedPoints: number;
    avgLateralPassPoints: number;
    bestMatchPoints: number;
    consistencyScore: number;
    totalSavePercentage: number;
    totalGSAx: number;
    totalGoalsAgainst: number;
    totalExpectedGoalsAgainst: number;
}

export const calculatePlayerMetrics = (kpis: PlayerKpi[]): PlayerMetrics => {
    if (kpis.length === 0) {
        return {
            avgPointsPerGame: 0,
            totalGames: 0,
            totalPoints: 0,
            avgOffensivePoints: 0,
            avgDefensivePoints: 0,
            avgTeamPlayPoints: 0,
            avgEffortPoints: 0,
            avgBonusPoints: 0,
            bestMatchPoints: 0,
            consistencyScore: 0,
        };
    }

    const totalPoints = kpis.reduce((sum, kpi) => sum + kpi.totalPoints, 0);
    const totalOffensivePoints = kpis.reduce((sum, kpi) => sum + kpi.offensiveData.points, 0);
    const totalDefensivePoints = kpis.reduce((sum, kpi) => sum + kpi.defensiveData.points, 0);
    const totalTeamPlayPoints = kpis.reduce((sum, kpi) => sum + kpi.teamPlayData.points, 0);
    const totalEffortPoints = kpis.reduce((sum, kpi) => sum + kpi.effortData.points, 0);
    const totalBonusPoints = kpis.reduce((sum, kpi) => sum + kpi.bonusData.points, 0);

    const avgPointsPerGame = totalPoints / kpis.length;
    const avgOffensivePoints = totalOffensivePoints / kpis.length;
    const avgDefensivePoints = totalDefensivePoints / kpis.length;
    const avgTeamPlayPoints = totalTeamPlayPoints / kpis.length;
    const avgEffortPoints = totalEffortPoints / kpis.length;
    const avgBonusPoints = totalBonusPoints / kpis.length;

    const bestMatchPoints = Math.max(...kpis.map((kpi) => kpi.totalPoints));

    const pointsArray = kpis.map((kpi) => kpi.totalPoints);
    const mean = avgPointsPerGame;
    const variance = pointsArray.reduce((sum, points) => sum + Math.pow(points - mean, 2), 0) / kpis.length;
    const stdDev = Math.sqrt(variance);
    const consistencyScore = mean > 0 ? Math.max(0, 100 - (stdDev / mean) * 100) : 0;

    return {
        avgPointsPerGame,
        totalGames: kpis.length,
        totalPoints,
        avgOffensivePoints,
        avgDefensivePoints,
        avgTeamPlayPoints,
        avgEffortPoints,
        avgBonusPoints,
        bestMatchPoints,
        consistencyScore,
    };
};

export const calculateGoalkeeperMetrics = (kpis: GoalkeeperKpi[]): GoalkeeperMetrics => {
    if (kpis.length === 0) {
        return {
            avgPointsPerGame: 0,
            totalGames: 0,
            totalPoints: 0,
            avgGSAxPoints: 0,
            avgHighDangerPoints: 0,
            avgLowDangerPoints: 0,
            avgScreenedPoints: 0,
            avgLateralPassPoints: 0,
            bestMatchPoints: 0,
            consistencyScore: 0,
            totalSavePercentage: 0,
            totalGSAx: 0,
            totalGoalsAgainst: 0,
            totalExpectedGoalsAgainst: 0,
        };
    }

    const totalPoints = kpis.reduce((sum, kpi) => sum + kpi.totalPoints, 0);
    const totalGSAxPoints = kpis.reduce((sum, kpi) => sum + (kpi.pointsGSAx.pointsGSAx || 0), 0);
    const totalHighDangerPoints = kpis.reduce((sum, kpi) => sum + (kpi.zone1.pointsHighDangerShots || 0), 0);
    const totalLowDangerPoints = kpis.reduce((sum, kpi) => sum + (kpi.zone4.pointsLowDangerShots || 0), 0);
    const totalScreenedPoints = kpis.reduce((sum, kpi) => sum + (kpi.screened.pointsScreenedShots || 0), 0);
    const totalLateralPassPoints = kpis.reduce((sum, kpi) => sum + (kpi.lateralPass.pointsLateralPassShots || 0), 0);

    const avgPointsPerGame = totalPoints / kpis.length;
    const avgGSAxPoints = totalGSAxPoints / kpis.length;
    const avgHighDangerPoints = totalHighDangerPoints / kpis.length;
    const avgLowDangerPoints = totalLowDangerPoints / kpis.length;
    const avgScreenedPoints = totalScreenedPoints / kpis.length;
    const avgLateralPassPoints = totalLateralPassPoints / kpis.length;

    const bestMatchPoints = Math.max(...kpis.map((kpi) => kpi.totalPoints));

    const pointsArray = kpis.map((kpi) => kpi.totalPoints);
    const mean = avgPointsPerGame;
    const variance = pointsArray.reduce((sum, points) => sum + Math.pow(points - mean, 2), 0) / kpis.length;
    const stdDev = Math.sqrt(variance);
    const consistencyScore = mean > 0 ? Math.max(0, 100 - (stdDev / mean) * 100) : 0;

    const totalGSAx = kpis.reduce((sum, kpi) => sum + (kpi.pointsGSAx.gsAx || 0), 0);
    const totalGoalsAgainst = kpis.reduce((sum, kpi) => sum + (kpi.pointsGSAx.ga || 0), 0);
    const totalExpectedGoalsAgainst = kpis.reduce((sum, kpi) => sum + (kpi.pointsGSAx.xg_GA || 0), 0);
    const avgSavePercentage = kpis.reduce((sum, kpi) => sum + (kpi.pointsGSAx.savePercentage || 0), 0) / kpis.length;

    return {
        avgPointsPerGame,
        totalGames: kpis.length,
        totalPoints,
        avgGSAxPoints,
        avgHighDangerPoints,
        avgLowDangerPoints,
        avgScreenedPoints,
        avgLateralPassPoints,
        bestMatchPoints,
        consistencyScore,
        totalSavePercentage: avgSavePercentage,
        totalGSAx,
        totalGoalsAgainst,
        totalExpectedGoalsAgainst,
    };
};

const fetchLatestPlayerData = async (playerId: string, teamId?: number) => {
    try {
        const response = await axiosInstance.get(`/0/${teamId ?? 0}/kpis/${playerId}`);
        return playerDataSchema.parse(response.data);
    } catch (error) {
        throw new Error(`Failed to fetch latest player data: ${error}`);
    }
};

const fetchPlayerData = async (playerId: string, tournamentId: number, teamId?: number) => {
    try {
        const response = await axiosInstance.get(`/${tournamentId}/${teamId ?? 0}/kpis/${playerId}`);
        return playerDataSchema.parse(response.data);
    } catch (error) {
        throw new Error(`Failed to fetch player data: ${error}`);
    }
};

export const latestPlayerDataQueryOptions = (playerId: string, teamId?: number) =>
    queryOptions({
        queryKey: ["player", playerId, "latest", teamId],
        queryFn: () => fetchLatestPlayerData(playerId, teamId),
        staleTime: 5 * 60 * 1000,
    });

export const playerDataQueryOptions = (playerId: string, tournamentId: number, teamId?: number) =>
    queryOptions({
        queryKey: ["player", playerId, tournamentId, teamId],
        queryFn: () => fetchPlayerData(playerId, tournamentId, teamId),
        staleTime: 5 * 60 * 1000,
    });
